import React from 'react';
import ReactDOM from 'react-dom';

import { isValidImageType, uploadFiles$ } from 'ub/control/asset-upload';
import UnsupportedFileUploadModal from 'ub/components/UnsupportedFileUploadModal';

const calcOffsetX = (eventOffset, page) => {
  const pageWidth = page.getContentWidth();
  const newOffset =
    eventOffset -
    ['.elements-pane', '.editor-divider-left', '.widget-panel']
      .map(e => document.querySelector(e).offsetWidth)
      .reduce((acc, cur) => acc + cur);
  if (newOffset < 0) {
    return 0;
  } else if (newOffset > pageWidth) {
    return pageWidth / 2;
  } else {
    return newOffset;
  }
};

const setWaitMessage = (fileIndex, totalFiles) => {
  window.editor.stopWaiting();
  window.editor.wait({
    message:
      totalFiles > 1 ? `Adding image ${fileIndex + 1} of ${totalFiles}...` : `Adding image...`,
  });
};

const uploadFiles = (files, event, authToken, parentElm) => {
  setWaitMessage(0, files.length);

  uploadFiles$(files, authToken, true)
    .map(({ asset }, index) => ({ asset, index }))
    .subscribe(
      ({ asset, index }) => {
        setWaitMessage(index + 1, files.length);

        if (asset) {
          const groupOffset = index * 15;

          window.lp.getModule('lp-pom-image').createAndInsertElement(
            window.editor.page,
            {
              container: parentElm,
              offset: {
                left: calcOffsetX(event.offsetX, window.editor.page) + groupOffset,
                top: event.offsetY + groupOffset,
              },
              center: true,
              autoscale: true,
              hideLoader: true,
            },
            asset
          );
        }
      },
      () => window.editor.stopWaiting(),
      () => window.editor.stopWaiting()
    );
};

const renderUnsupportedFilesModal = (fileCount, invalidFiles) => {
  const container = document.createElement('div');
  document.body.appendChild(container);

  ReactDOM.render(
    React.createElement(UnsupportedFileUploadModal, {
      fileCount,
      unsupportedFilenames: invalidFiles.map(file => file.name),
      onClose: () => {
        ReactDOM.unmountComponentAtNode(container);
        container.parentNode.removeChild(container);
      },
    }),
    container
  );
};

const highlight = elm => elm.setAttribute('highlight', '');

const removeHighlight = canvasElement => {
  canvasElement.removeAttribute('data-dropzone');
  Array.from(document.querySelectorAll('[highlight]')).forEach(e => e.removeAttribute('highlight'));
};

const handleCanvasDrop = event => {
  const authToken = document.querySelector('meta[name="csrf-token"]').content;
  const parentElm = event.target.closest('.lp-pom-box, .lp-pom-block');

  if (!parentElm || !authToken) {
    return;
  }

  const files = Array.from(event.dataTransfer.files);
  const validFiles = files.filter(file => isValidImageType(file.type));
  const invalidFiles = files.filter(file => !isValidImageType(file.type));

  if (invalidFiles.length) {
    renderUnsupportedFilesModal(files.length, invalidFiles);
  }

  uploadFiles(validFiles, event, authToken, parentElm);
};

const handleBodyDragEnter = canvasElement => event => {
  // Called when a drag crosses the boundary into any element on the page.
  // Update the drop highlight.
  event.preventDefault();

  removeHighlight(canvasElement);

  // When this attribute is present, CSS applies a `pointer-events: none` declaration to all canvas
  // elements that are not valid containers - e.g. everything but sections and boxes. This causes
  // the `drop` and `dragenter` events to only fire on the latter.
  canvasElement.setAttribute('data-dropzone', '');

  const parentTargetElement = event.target.closest(
    '.lp-element.lp-pom-box, .lp-element.lp-pom-block'
  );

  const highlightElements = parentTargetElement
    ? [parentTargetElement]
    : Array.from(canvasElement.querySelectorAll('.lp-pom-block'));

  highlightElements.forEach(highlight);
};

const handleBodyMouseLeave = canvasElement => () => {
  // Called when mouse leaves the page entirely.
  removeHighlight(canvasElement);
};

const handleBodyDrop = canvasElement => event => {
  event.preventDefault();
  removeHighlight(canvasElement);
};

const handleBodyDragover = event => {
  // Called repeatedly while the user is dragging a file anywhere on the page.
  // Update the system drop behaviour and cursor via dropEffect.
  event.preventDefault();

  const someFilesValid = Array.from(event.dataTransfer.items)
    .map(item => item.type)
    .some(isValidImageType);

  if (someFilesValid && event.target.closest('.lp-element.lp-pom-box, .lp-element.lp-pom-block')) {
    event.dataTransfer.effectAllowed = 'copyMove';
    event.dataTransfer.dropEffect = 'copy';
  }
};

export default function initFileDrop(body, canvasElement) {
  // Dragging files over the body will activate highlights, but only drops onto `canvasElement` will
  // be accepted.
  body.addEventListener('drop', handleBodyDrop(canvasElement));
  body.addEventListener('dragover', handleBodyDragover);
  body.addEventListener('dragenter', handleBodyDragEnter(canvasElement));
  body.addEventListener('mouseleave', handleBodyMouseLeave(canvasElement));

  canvasElement.addEventListener('drop', handleCanvasDrop);
}



// WEBPACK FOOTER //
// ./src/ui/file_drag_and_drop.js